/**
 * GA4 helper for the platform (CRA — REACT_APP_*).
 * Loads GTM and/or direct gtag when env vars are set.
 */

const SESSION_KEY = 'ga_session_id';

export function getOrCreateSessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function isDevEnv(environment) {
  return environment === 'development' || environment === 'dev';
}

export function initGA4() {
  if (typeof window === 'undefined') return;

  const measurementId = (process.env.REACT_APP_GA4_MEASUREMENT_ID || '').trim();
  const gtmId = (process.env.REACT_APP_GTM_ID || '').trim();
  const environment = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  const sessionId = getOrCreateSessionId();

  if (!measurementId && !gtmId) {
    if (isDevEnv(environment)) {
      console.warn('⚠ GA4 not initialized: set REACT_APP_GTM_ID or REACT_APP_GA4_MEASUREMENT_ID');
    }
    return;
  }

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  // GTM container (tags/triggers live here)
  if (gtmId && !document.querySelector(`script[src*="gtm.js?id=${gtmId}"]`)) {
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
      custom_env: environment,
      session_id: sessionId,
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);
  }

  // Direct GA4 measurement ID (optional; works without GTM tags)
  if (measurementId) {
    if (!document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);
    }
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      custom_env: environment,
      session_id: sessionId,
      debug_mode: isDevEnv(environment),
    });
  }

  if (isDevEnv(environment)) {
    console.log(
      `✓ GA4 initialized${gtmId ? ` GTM=${gtmId}` : ''}${measurementId ? ` GA4=${measurementId}` : ''} (${environment})`,
    );
  }
}

export function trackEvent(eventName, eventData = {}) {
  const environment = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  const sessionId = sessionStorage.getItem(SESSION_KEY) || getOrCreateSessionId();

  const enrichedData = {
    event_category: eventData.event_category || 'general',
    event_label: eventData.event_label || '',
    section_id: eventData.section_id || '',
    user_session_id: sessionId,
    timestamp: new Date().toISOString(),
    environment,
    ...eventData,
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...enrichedData });
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, enrichedData);
  }

  if (isDevEnv(environment)) {
    console.log('📊 Event tracked:', { event: eventName, data: enrichedData });
  }
}

export function useTrackEvent() {
  return { trackEvent };
}
